'use client';

import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

const FOUNDERS = [
    {
        id: 1,
        name: 'Abdul Sohail Samad',
        role: 'Founder & Technical Lead',
        expertise: ['Full-Stack Arch.', 'Next.js Expert', 'Scalable Systems'],
        image: "/abdulsohailsamad.jpg",
        alt: 'Abdul Sohail Samad - Technical Lead',
        description: 'Architecting the digital backbone of modern enterprises. Expert in translating complex business logic into seamless, high-performance web applications.',
        social: [{ icon: 'CodeBracketIcon', link: '#' }, { icon: 'CpuChipIcon', link: '#' }]
    }
];

const MobileFoundersSection = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollXProgress } = useScroll({ container: containerRef });
    const [activeIndex, setActiveIndex] = useState(0);

    const onScroll = () => {
        if (containerRef.current) {
            const scrollLeft = containerRef.current.scrollLeft;
            const width = containerRef.current.offsetWidth;
            const index = Math.round(scrollLeft / (width * 0.85));
            const newIndex = Math.min(index, FOUNDERS.length - 1);
            if (newIndex !== activeIndex) {
                setActiveIndex(newIndex);
            }
        }
    };

    // Parallax & Transition Effects
    const backgroundX = useTransform(scrollXProgress, [0, 1], ['0%', '-20%']);
    const backgroundopacity = useTransform(scrollXProgress, [0, 0.5, 1], [0.8, 0.5, 0.8]);
    const lineProgress = useTransform(scrollXProgress, [0, 1], [0, 100]);

    return (
        <section className="relative py-20 bg-slate-50 overflow-hidden">
            {/* --- CINEMATIC BACKGROUND --- */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div
                    style={{ x: backgroundX, opacity: backgroundopacity }}
                    className="absolute -top-20 -left-20 w-80 h-80 bg-indigo-500/10 rounded-full blur-[80px] will-change-transform"
                />
                <motion.div
                    style={{ x: useTransform(scrollXProgress, [0, 1], ['0%', '20%']) }}
                    className="absolute top-1/2 -right-20 w-80 h-80 bg-blue-500/10 rounded-full blur-[80px] will-change-transform"
                />
                <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-[0.03]" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                {/* Header */}
                <div className="text-center mb-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        <span className="inline-block text-[10px] font-bold text-indigo-600 tracking-[0.2em] uppercase mb-2">Leadership</span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
                        className="text-3xl font-black text-slate-900 tracking-tight"
                    >
                        Meet the <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500">Founder</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                        className="text-sm text-slate-500 mt-4 max-w-[280px] mx-auto leading-relaxed"
                    >
                        Driven by a passion for elite technical architecture and aggressive market strategy. We don't just build software; we engineer dominance.
                    </motion.p>
                </div>

                {/* --- HORIZONTAL PARALLAX DECK --- */}
                <div className="relative">
                    {/* Progress Line Indicator */}
                    <div className="absolute top-0 left-6 right-6 h-0.5 bg-indigo-500 z-0 rounded-full mb-8" />

                    <div
                        ref={containerRef}
                        onScroll={onScroll}
                        className="flex overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pt-6 pb-8 gap-4 px-4"
                    >
                        {FOUNDERS.map((founder, index) => (
                            <div key={founder.id} className="snap-center shrink-0 w-[85vw] max-w-[360px] perspective-1000 first:pl-2 last:pr-2">
                                <motion.div
                                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 0.8, delay: 0.2 + (index * 0.2), ease: "easeOut" }}
                                    className="relative group h-full"
                                >
                                    {/* Card Container - Full Image Layout */}
                                    <div className="bg-slate-900 rounded-[32px] overflow-hidden shadow-xl shadow-slate-200/50 border border-slate-100 h-[520px] flex flex-col relative z-10 transition-transform duration-300">

                                        {/* Full Height Image Layer */}
                                        <div className="absolute inset-0 z-0">
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent z-10" />
                                            <img
                                                src={founder.image}
                                                alt={founder.alt}
                                                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                                            />
                                        </div>

                                        {/* Content Overlay */}
                                        <div className="absolute inset-x-0 bottom-0 z-20 p-8 flex flex-col justify-end h-full">

                                            {/* Role Badge - Top Left inside card */}
                                            <div className="absolute top-6 left-6">
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-md rounded-full border border-white/20 shadow-lg">
                                                    <Icon name={index === 0 ? "CodeBracketIcon" : "ChartBarIcon"} size={14} className="text-white" />
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">{founder.role}</span>
                                                </div>
                                            </div>

                                            <div className="relative">
                                                <h3 className="text-3xl font-bold text-white mb-3 tracking-tight">{founder.name}</h3>
                                                <p className="text-sm text-slate-300 leading-relaxed mb-6 line-clamp-3 font-medium">
                                                    {founder.description}
                                                </p>

                                                {/* Footer: Skills & Social */}
                                                <div>
                                                    <div className="flex flex-wrap gap-2 mb-4">
                                                        {founder.expertise.slice(0, 3).map((skill, i) => (
                                                            <span key={i} className="text-[10px] font-bold uppercase tracking-wide text-white bg-white/10 border border-white/10 px-2.5 py-1 rounded-md backdrop-blur-sm">
                                                                {skill}
                                                            </span>
                                                        ))}
                                                    </div>

                                                    <div className="flex items-center justify-between border-t border-white/10 pt-4">
                                                        <span className="text-xs font-medium text-slate-400">Connect</span>
                                                        <div className="flex gap-2">
                                                            {founder.social.map((s, i) => (
                                                                <button key={i} className="p-2 rounded-full bg-white/10 hover:bg-white text-white hover:text-slate-900 transition-colors backdrop-blur-md">
                                                                    <Icon name={s.icon as any} size={16} />
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination Dots */}
                    <div className="flex justify-center gap-2 mt-4">
                        {FOUNDERS.map((_, index) => (
                            <div
                                key={index}
                                className={`h-1.5 rounded-full transition-all duration-300 ${activeIndex === index ? 'w-8 bg-indigo-600' : 'w-1.5 bg-slate-300'}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MobileFoundersSection;
